import { assertNever, checkFail } from '../core.ts';
import { isArray, isObject, isPresent, mapRecord } from '../identity.ts';
import { DateTime, dateToday, standardTime } from '../time.ts';
import { Scalar } from '../units.ts';
import {
  type AnyTypeDescriptor,
  type Descriptor,
  type ElementType,
  type StrictPropertyList,
  type Struct,
  type TypeDescriptor,
} from './descriptor.ts';

function cloneProp<T>(v: T, p: TypeDescriptor<T>): unknown {
  if (p.t === 'obj') {
    if (isObject(v)) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return cloneProps(v, p.el);
    }
  } else if (p.t === 'array') {
    if (isArray(v)) {
      return v.map((e) => cloneProp(e as ElementType, p.el));
    }
  } else if (p.t === 'dict') {
    if (isObject(v)) {
      return mapRecord(v, (e) => cloneProp(e, p.el as AnyTypeDescriptor));
    }
  }
  return v;
}

export function cloneProps<T extends Struct>(v: Partial<T>, d: StrictPropertyList<T>) {
  return Object.fromEntries(
    d
      .map((p) => {
        const prop = cloneProp(v[p.k], p as AnyTypeDescriptor);
        return isPresent(prop) ? [p.k, prop] : undefined;
      })
      .filter(isPresent),
  ) as T;
}

export function clone<T extends Struct>(v: T, d: Descriptor<T>): T;
export function clone<T extends Struct>(v: Partial<T>, d: Descriptor<T>): Partial<T>;
export function clone<T extends Struct>(v: Partial<T>, d: Descriptor<T>) {
  return cloneProps(v, d.props);
}

export function defaultProp(p: AnyTypeDescriptor) {
  if (p.t === 'obj' || p.t === 'dict') {
    return {};
  } else if (p.t === 'array') {
    return [];
  } else if (!(p.req ?? false)) {
    return undefined;
  }
  switch (p.t) {
    case 'bool':
      return false;
    case 'number':
      return p.enum?.at(0) ?? 0;
    case 'string':
      return p.enum?.at(0) ?? '';
    case 'datetime':
      switch (p.st ?? 'timestamp') {
        case 'timestamp':
          return DateTime.now();
        case 'time':
          return standardTime(DateTime.now());
        case 'date':
        case 'date-year':
          return dateToday();
      }
      break;
    case 'scalar':
      return Scalar.from(p.u, 0);
    case 'ref':
      return checkFail(`Unexpectedly required property type: ${p.t}`);
  }
  assertNever(p);
}

function defaultProps<T extends Struct>(d: StrictPropertyList<T>) {
  return Object.fromEntries(d.map((p) => [p.k, defaultProp(p as AnyTypeDescriptor)])) as T;
}

export function createDefault<T extends Struct>(desc: Descriptor<T>) {
  return defaultProps(desc.props);
}
